/**
 * CharmOptionsRenderer - Lucky Charm node options configuration
 *
 * Handles theme, background, font, and other customization options.
 * Supports field mapping via FieldSourceToggle for the phrase field.
 */

import React from 'react';
import { Input } from '@/components/ui/Input';
import { useI18n } from '@/i18n';
import {
  useLuckyCharmThemes,
  useLuckyCharmImages,
} from '@/features/playground/hooks/usePlayground';
import { FontPicker } from '@/components/FontPicker';
import { FieldSourceToggle } from '../FieldSourceToggle';
import type { BaseCustomRendererProps } from './types';
import { configFieldKey } from './types';

export const CharmOptionsRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  // Fetch Lucky Charm resources
  const currentThemeId = nodeData.background_set_id as string | undefined;
  const { data: themes = [], isLoading: themesLoading } = useLuckyCharmThemes();
  const { data: images = [], isLoading: imagesLoading } = useLuckyCharmImages(
    currentThemeId || undefined
  );
  const { t } = useI18n();

  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  // Create field handlers for phrase field
  const phraseFieldKey = configFieldKey('phrase');
  const phraseHandlers = fieldSourceHandlersFactory?.createFieldHandlers(phraseFieldKey);

  return (
    <div className="space-y-4">
      {/* Required: Phrase */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{t('imageWorkflows.charmOptions.requiredFields')}</span>
        </div>

        <FieldSourceToggle
          fieldKey={phraseFieldKey}
          label={t('imageWorkflows.charmOptions.phraseContent')}
          description={t('imageWorkflows.charmOptions.phraseHint')}
          required={true}
          availableSources={availableSources}
          customInputs={customInputs}
          contextVariables={contextVariables}
          startVariables={startVariables}
          mapping={phraseHandlers?.mapping}
          onMappingChange={phraseHandlers?.onMappingChange || (() => {})}
          onKeyPathChange={phraseHandlers?.onKeyPathChange || (() => {})}
          onMappingRemove={phraseHandlers?.onMappingRemove || (() => {})}
          disabled={isDisabled}
          compatibleTypes={['string']}
        >
          <textarea
            value={(nodeData.phrase as string) || ''}
            onChange={(e) => handleUpdate('phrase', e.target.value || undefined)}
            rows={2}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
            placeholder={t('imageWorkflows.charmOptions.phrasePlaceholder')}
          />
        </FieldSourceToggle>
      </div>

      {/* Customization Options */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{t('imageWorkflows.charmOptions.imageGenOptions')}</span>
          <span className="text-xs text-gray-400">{t('imageWorkflows.charmOptions.optional')}</span>
        </div>

        {/* Emotion Trend */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.charmOptions.emotionTrend')}</label>
          <select
            value={(nodeData.emotion_trend as string) || ''}
            onChange={(e) => handleUpdate('emotion_trend', e.target.value || undefined)}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
          >
            <option value="">{t('imageWorkflows.charmOptions.auto')}</option>
            <option value="积极">{t('imageWorkflows.charmOptions.positive')}</option>
            <option value="平静">{t('imageWorkflows.charmOptions.calm')}</option>
            <option value="希望">{t('imageWorkflows.charmOptions.hopeful')}</option>
            <option value="感恩">{t('imageWorkflows.charmOptions.grateful')}</option>
            <option value="忧虑">{t('imageWorkflows.charmOptions.concerned')}</option>
          </select>
        </div>

        {/* Chart ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.charmOptions.chartId')}</label>
          <Input
            value={(nodeData.chart_id as string) || ''}
            onChange={(e) => handleUpdate('chart_id', e.target.value || undefined)}
            placeholder={t('imageWorkflows.charmOptions.chartIdPlaceholder')}
            disabled={isDisabled}
          />
        </div>

        {/* Background Theme (Set) Selector — Card-based */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('imageWorkflows.charmOptions.backgroundTheme')}
            {themesLoading && <span className="ml-2 text-gray-400 text-xs">{t('imageWorkflows.charmOptions.loading')}</span>}
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {/* "Any" option */}
            <button
              type="button"
              disabled={themesLoading || isDisabled}
              onClick={() => {
                handleUpdate('background_set_id', undefined);
                handleUpdate('background_image_id', undefined);
              }}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                !nodeData.background_set_id
                  ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } disabled:opacity-50`}
            >
              <span className="font-medium text-gray-700">{t('imageWorkflows.charmOptions.allThemes')}</span>
            </button>
            {/* Theme cards */}
            {themes.map((theme) => {
              const isSelected = nodeData.background_set_id === theme.id;
              return (
                <button
                  key={theme.id}
                  type="button"
                  disabled={themesLoading || isDisabled}
                  onClick={() => {
                    handleUpdate('background_set_id', theme.id);
                    handleUpdate('background_image_id', undefined);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                    isSelected
                      ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">{theme.display_name}</span>
                    <div className="flex items-center gap-1.5">
                      {theme.has_overlay && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                          {t('imageWorkflows.charmOptions.overlayBadge')}
                        </span>
                      )}
                      {theme.font_color && theme.font_color !== 'black' && theme.font_color !== 'auto' && (
                        <span
                          className="inline-block w-3 h-3 rounded-full border border-gray-300"
                          style={{ backgroundColor: theme.font_color }}
                          title={`${t('imageWorkflows.charmOptions.fontColorLabel')}: ${theme.font_color}`}
                        />
                      )}
                      {theme.font_color === 'auto' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                          {t('imageWorkflows.charmOptions.autoColor')}
                        </span>
                      )}
                      {(theme.rotation_angle ?? 0) !== 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                          {theme.rotation_angle}°
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{theme.image_count} {t('imageWorkflows.charmOptions.imagesUnit')}</span>
                    </div>
                  </div>
                  {theme.description && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{theme.description}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Background Image and Font Selectors */}
        <div className="grid grid-cols-2 gap-3">
          {/* Background Image Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('imageWorkflows.charmOptions.backgroundImage')}
              {imagesLoading && <span className="ml-2 text-gray-400 text-xs">...</span>}
            </label>
            <select
              value={(nodeData.background_image_id as string) || ''}
              onChange={(e) => handleUpdate('background_image_id', e.target.value || undefined)}
              disabled={imagesLoading || isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
            >
              <option value="">
                {t('imageWorkflows.charmOptions.random')}{nodeData.background_set_id ? ` (${t('imageWorkflows.charmOptions.randomCurrentTheme')})` : ''}
              </option>
              {images.map((img) => (
                <option key={img.id} value={img.id} title={img.filename}>
                  {img.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Font Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('imageWorkflows.charmOptions.font')}
            </label>
            <FontPicker
              value={(nodeData.font_id as number | undefined) ?? null}
              onChange={(fontId) => handleUpdate('font_id', fontId ?? undefined)}
              fallbackLabel="使用 pack 默认"
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharmOptionsRenderer;
